import { ApolloQueryResult } from '@apollo/client';
import { useTranslation } from 'next-i18next';
import React, { ReactElement } from 'react';
import { FdrImg } from '@hotelplan/fdr.regular.basis.fdr-img';
import { useFetchingPagination } from '@hotelplan/libs.hooks';
import { calculateNextPage } from '@hotelplan/libs.utils';
import { FdrImage } from '@hotelplan/supergraph-api';
import { SectionMainTitle } from 'components/domain/customizable-slider/CardsSection.styles';
import { FdrTeaserPlaceholder } from 'components/domain/fdr-teaser-card';
import { LoadMoreButtonWithWrapper } from 'components/domain/recommendations/LoadMoreButton';
import { RecommendationsGrid } from 'components/domain/recommendations/Recommendations.common';
import { fdrBlogCriteria } from 'components/util/criterias/blog.dmc';
import { FdrBlogArticleFragment } from 'schemas/fragment/blog-articles/fdr-all-blog-articles.generated';
import {
  FdrBlogArticlesQuery,
  useFdrBlogArticlesQuery,
} from 'schemas/query/blog-article-overview/fdr-blog-overview-articles.generated';
import { FdrAuthorDisrupter } from './fdr-author-disrupter';
import {
  FdrBlogArticleImage,
  FdrBlogArticles,
  FdrBlogArticlesWrapper,
} from './fdr-blog-overview.styles';

const ARTICLES_PER_PAGE = 9;

export function FdrBlogArticlesOverview(): ReactElement {
  const [t] = useTranslation('blogs');

  const queryResult = useFdrBlogArticlesQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      blogCriteria: fdrBlogCriteria,
      pagination: {
        page: 0,
        perPage: ARTICLES_PER_PAGE,
      },
    },
  });

  const { pagination, blogArticles } =
    queryResult?.data?.fdrAllBlogArticles || {};
  const { page, perPage, total } = pagination || {};

  const nextPageNumber = calculateNextPage(page, perPage, total);

  const { items, hasMore, nextPage } = useFetchingPagination<
    FdrBlogArticleFragment,
    Promise<ApolloQueryResult<FdrBlogArticlesQuery>>
  >(blogArticles || [], {
    page: page,
    perPage: perPage,
    total: total,
    async fetchMore() {
      return await queryResult?.fetchMore({
        variables: {
          pagination: {
            page: nextPageNumber,
            perPage: ARTICLES_PER_PAGE,
          },
        },
      });
    },
  });

  if (!items?.length) return null;

  return (
    <FdrBlogArticlesWrapper>
      <FdrBlogArticles>
        <SectionMainTitle className="main-title">
          {t('blogs:articles.overview.title')}
        </SectionMainTitle>
        <RecommendationsGrid className="articles">
          {items.map(function (blogArticle) {
            return (
              <>
                {blogArticle && (
                  <div
                    key={blogArticle.publicId}
                    style={{ position: 'relative' }}
                  >
                    <FdrBlogArticleImage
                      data-id="fdr-blog-article-card"
                      img={<FdrImg image={blogArticle.hero} />}
                      cfg={{
                        title: blogArticle.title,
                        link: blogArticle.webMeta.link,
                      }}
                    />
                    {blogArticle.employee && (
                      <FdrAuthorDisrupter
                        name={blogArticle.employee.name}
                        image={blogArticle.employee.image as FdrImage}
                      />
                    )}
                  </div>
                )}
              </>
            );
          })}
          {queryResult?.loading ? (
            <>
              {Array.from({ length: ARTICLES_PER_PAGE }).map(function (
                _,
                index
              ) {
                return <FdrTeaserPlaceholder key={index} />;
              })}
            </>
          ) : null}
        </RecommendationsGrid>
        {hasMore ? (
          <LoadMoreButtonWithWrapper onClick={nextPage}>
            {t('blogs:load.more')}
          </LoadMoreButtonWithWrapper>
        ) : null}
      </FdrBlogArticles>
    </FdrBlogArticlesWrapper>
  );
}
